import TileLayer from "ol/layer/Tile";
import { TileWMS, XYZ } from "ol/source";
import { getImageScale } from "./helpers";
import { Feature } from "ol";
import { getStyleForBasicFeature } from "./styles/mapStyles";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { Icon, Style } from "ol/style";
import GeoJSON from "ol/format/GeoJSON";
import WKT from "ol/format/WKT";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { store } from "../../../stateManagement/store";
import { rosreestrLayersSettings } from "./mapFunctions/rosreestrFunctions/rosreestrFunctions";
import { IndorImages, getIndorImageSrc } from "../../../features";

// Основная подложка карты
export function getRasterLayer(highContrastModeIsEnabled: boolean) {
    return new TileLayer({
        preload: Infinity,
        zIndex: 0,
        className: highContrastModeIsEnabled ? "grayscale" : "",
        source: new XYZ({
            url: 'https://map.indorsoft.ru/hot/{z}/{x}/{y}.png',
            tilePixelRatio: getImageScale().imageScale,
            maxZoom: 20
        }),
    });
}

// Основной слой дорожной сети
export function getAllObjectsLayer(env?: string) {
    const currentConfig = getItemWebStorage('currentConfig')
    const mapSettings = getItemWebStorage('mapSettings') ?? {}
    const auxOpacity = mapSettings?.auxiliaryLayersOpacityState ?? {}
    const allObjectsOpacity = typeof auxOpacity?.allObjects === 'number' ? auxOpacity.allObjects : 1
    const { wmsServer, workspace, mainLayer } = currentConfig ?? {}
    return new TileLayer({
        className: 'aux-allObjects',
        preload: Infinity,
        zIndex: 100,
        opacity: allObjectsOpacity,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + '/wms',
            params: {
                'LAYERS': `${workspace}:${mainLayer}`, // Указать нужные слои
                'TILED': 'true',
                'FORMAT': 'image/png',
                'SRS': 'EPSG:3857',
                'dpi': getImageScale().dpi?.toString(),
                'env': env
            },
        })
    });
}

// Обычный слой для типа объекта
export function getBasicObjectTypeLayer(layers: any, wmsServer: string, wmsVersion: string, workspace: string, layerFilter?: any, styles?: string, env?: string) {
    return new TileLayer({
        preload: Infinity,
        zIndex: 300,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + 'wms',
            params: {
                'VERSION': wmsVersion,
                'LAYERS': Array.isArray(layers) ? `${workspace}:${layers.join(',')}` : (workspace + ':' + layers),
                'TILED': true,
                'FORMAT': 'image/png',
                'SRS': 'EPSG:3857',
                'CQL_FILTER': layerFilter || null,
                'STYLES': styles || null,
                'dpi': getImageScale().dpi?.toString(),
                'env': env || null
            },
        })
    })
}

function getConfiguredWMSLayer(layerConfig: any, currentConfig: any) {
    const {
        wmsServer,
        wmsVersion,
        workspace: defaultWorkspace
    } = currentConfig ?? {};
    const workspace = layerConfig?.workspace ?? defaultWorkspace;
    const configuredLayerName = layerConfig?.layerName;
    if (!wmsServer || !wmsVersion || !workspace || !configuredLayerName) return null;

    const layersParam = Array.isArray(configuredLayerName)
        ? configuredLayerName.map((layerName: string) => layerName.includes(':') ? layerName : `${workspace}:${layerName}`).join(',')
        : (configuredLayerName.includes(':') ? configuredLayerName : `${workspace}:${configuredLayerName}`);

    const layer = new TileLayer({
        preload: Infinity,
        zIndex: typeof layerConfig?.zIndex === 'number' ? layerConfig.zIndex : 300,
        className: layerConfig?.className ?? 'configured-page-layer',
        opacity: typeof layerConfig?.opacity === 'number' ? layerConfig.opacity : 1,
        visible: layerConfig?.visibleByDefault !== false,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + 'wms',
            params: {
                'VERSION': wmsVersion,
                'LAYERS': layersParam,
                'TILED': true,
                'FORMAT': 'image/png',
                'SRS': 'EPSG:3857',
                'CQL_FILTER': layerConfig?.layerFilter ?? null,
                'STYLES': layerConfig?.styles ?? null,
                'dpi': getImageScale().dpi?.toString(),
                'env': layerConfig?.env ?? null
            },
        })
    });

    if (layerConfig?.clickInteraction) {
        layer.set('clickInteraction', layerConfig.clickInteraction);
    }

    return layer;
}

// Источник с аэросъемкой
export function getAeroSource(aeroLayerUrl: any) {
    return new XYZ({
        tilePixelRatio: getImageScale().imageScale,
        projection: 'EPSG:3857',
        tileUrlFunction: (tileCoord) => {
            const z = tileCoord[0];
            const x = tileCoord[1];
            const y = -tileCoord[2];
            function newAeroLink(x: any, y: any, pyramidLevel?: any) {
                if (pyramidLevel === undefined) pyramidLevel = 0;
                let path = '';
                const power = Math.pow(100, pyramidLevel);
                const px = Math.floor(Math.abs(x) / power);
                const py = Math.floor(Math.abs(y) / power);
                if (px >= 100 || py >= 100) path = newAeroLink(x, y, pyramidLevel + 1);
                if (pyramidLevel > 0 && px + py > 0) {
                    let xx = '';
                    let yy = '';
                    for (let i = 0; i < pyramidLevel; i++) {
                        xx += 'xx';
                        yy += 'yy';
                    }
                    path = path + px + xx + '-' + py + yy + '/';
                }
                return path;
            }
            const pyramidFolderName = newAeroLink(x, y);
            const path = z + '/' + pyramidFolderName + 'tile-' + x + '' + y + '.png';
            return aeroLayerUrl + path;
        },
    });
}

// Источник в XYZ
export function getDefaultXYZSource(layerUrl: any) {
    return new XYZ({
        url: layerUrl,
        tilePixelRatio: getImageScale().imageScale,
    })
}

// Слой аэрофотосъёмка «Индор-Авиа»
export function getAeroLayer() {
    const aeroLayerUrl = 'https://geo.indorsoft.ru/rasters/layer.aero/'
    const mapSettings = getItemWebStorage('mapSettings') ?? {}
    const auxOpacity = mapSettings?.auxiliaryLayersOpacityState ?? {}
    const indorOpacity = typeof auxOpacity?.indorAvia === 'number' ? auxOpacity.indorAvia : 1
    return new TileLayer({
        className: 'aux-indorAvia',
        zIndex: 20,
        source: getAeroSource(aeroLayerUrl),
        visible: true,
        opacity: indorOpacity,
    });
}

// Слой спутник Esri World Imagery
export function getEsriWorldImageryLayer() {
    const mapSettings = getItemWebStorage('mapSettings') ?? {}
    const auxOpacity = mapSettings?.auxiliaryLayersOpacityState ?? {}
    const esriWorldImageryOpacity = typeof auxOpacity?.esriWorldImagery === 'number' ? auxOpacity.esriWorldImagery : 1
    return new TileLayer({
        className: 'aux-esriWorldImagery',
        zIndex: 10,
        visible: true,
        preload: Infinity,
        maxZoom: 19,
        opacity: esriWorldImageryOpacity,
        source: new XYZ({
            url:
                'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            attributions: 'Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
            crossOrigin: 'anonymous',
        }),
    });
}

// Дополнительные уникальные слои для базы
export function applyMapSettingsAdditionalLayers(layersDisplayState: any, currentConfig: any, layersArray?: any) {
    Object.keys(layersDisplayState).map((key) => {
        if (layersDisplayState[key]) {
            let layerObject = currentConfig?.mapSettingsAdditionalLayers?.layers.find((layer: any) => layer?.description?.id === key || layer?.name === key)
            let layerSource
            if (layerObject?.description.type === 'XYZ') {
                layerSource = getDefaultXYZSource(layerObject?.description.url)
            }
            if (layerObject?.description.type === 'Aero') {
                layerSource = getAeroSource(layerObject?.description.url)
            }
            const zIndexFromConfig = layerObject?.zIndex ?? layerObject?.description?.zIndex
            const mapSettings = getItemWebStorage('mapSettings') ?? {}
            const additionalOpacityState = mapSettings?.additionalLayersOpacityState ?? {}
            const layerOpacity = typeof additionalOpacityState?.[key] === 'number' ? additionalOpacityState[key] : 1
            const additionalLayer = new TileLayer({
                className: (layerObject?.className ? `${layerObject.className} ` : '') + `additional-${key}`,
                source: layerSource,
                visible: true,
                zIndex: typeof zIndexFromConfig === 'number' ? zIndexFromConfig : 100,
                opacity: layerOpacity,
            });
            if (layersArray) layersArray.push(additionalLayer)
        }
    })
    return layersArray
}

export function mapPageAdditionalLayerScopeMatches(scope: any, params: { mapName?: string; mapOption?: string; pathname?: string }) {
    if (!scope) return false;
    if (scope?.mapName && scope.mapName !== params.mapName) return false;
    if (scope?.mapOption && scope.mapOption !== params.mapOption) return false;
    if (scope?.routePath && scope.routePath !== params.pathname) return false;
    return true;
}

export function getMatchedMapPageAdditionalLayerConfigs(currentConfig: any, params: { mapName?: string; mapOption?: string; pathname?: string }) {
    const pageConfigs = Array.isArray(currentConfig?.mapPageAdditionalLayers) ? currentConfig.mapPageAdditionalLayers : [];
    return pageConfigs.filter((pageConfig: any) => mapPageAdditionalLayerScopeMatches(pageConfig?.scope, params));
}

export function getMapPageAdditionalLayers(currentConfig: any, params: { mapName?: string; mapOption?: string; pathname?: string }) {
    const pageConfigs = getMatchedMapPageAdditionalLayerConfigs(currentConfig, params);
    const layers: any[] = [];

    pageConfigs.forEach((pageConfig: any) => {
        const configuredLayers = Array.isArray(pageConfig?.layers) ? pageConfig.layers : [];

        configuredLayers.forEach((layerConfig: any) => {
            if (layerConfig?.visibleByDefault === false) return;

            let layer = null;
            if (layerConfig?.type === 'WMS') {
                layer = getConfiguredWMSLayer(layerConfig, currentConfig);
            } else if (layerConfig?.type === 'XYZ' && layerConfig?.url) {
                layer = new TileLayer({
                    className: layerConfig?.className ?? 'configured-page-layer',
                    source: getDefaultXYZSource(layerConfig.url),
                    visible: true,
                    zIndex: typeof layerConfig?.zIndex === 'number' ? layerConfig.zIndex : 100,
                    opacity: typeof layerConfig?.opacity === 'number' ? layerConfig.opacity : 1,
                });
            } else if (layerConfig?.type === 'Aero' && layerConfig?.url) {
                layer = new TileLayer({
                    className: layerConfig?.className ?? 'configured-page-layer',
                    source: getAeroSource(layerConfig.url),
                    visible: true,
                    zIndex: typeof layerConfig?.zIndex === 'number' ? layerConfig.zIndex : 100,
                    opacity: typeof layerConfig?.opacity === 'number' ? layerConfig.opacity : 1,
                });
            }

            if (layer) layers.push(layer);
        });
    });

    return layers;
}

// Основной слой дорожной сети
export function getRosreestrLayers() {
    const rosreestrLayersDisplayState: any = store.getState().mapSettings.rosreestrLayersDisplayState
    const rosreestrLayersOpacityState: any = store.getState().mapSettings.rosreestrLayersOpacityState
    let newRosreestrLayers: any = []
    if (rosreestrLayersDisplayState) {
        const rosreestrLayersDisplayStateActiveItems = Object.keys(rosreestrLayersDisplayState).filter((layerIndex: any) => rosreestrLayersDisplayState[layerIndex] && !layerIndex.includes('Labels'))
        rosreestrLayersDisplayStateActiveItems.map((simpleKey: any) => {
            const layerName: any = (rosreestrLayersSettings as any)[simpleKey]?.name
            const layerOpacity = typeof rosreestrLayersOpacityState?.[simpleKey] === 'number' ? rosreestrLayersOpacityState[simpleKey] : 1
            const rosreestrLayer = new TileLayer({
                preload: Infinity,
                zIndex: 500,
                className: `rosreestr-${simpleKey}`,
                opacity: layerOpacity,
                source: new TileWMS({
                    serverType: 'geoserver',
                    url: `https://nspd.gov.ru/api/aeggis/v4/${layerName}/wms`,
                    params: {
                        'VERSION': '1.3.0',
                        'LAYERS': layerName,
                        'TILED': 'true',
                        'FORMAT': 'image/png',
                        'CRS': 'EPSG:3857',
                        'dpi': getImageScale().dpi?.toString()
                    },
                })
            });
            newRosreestrLayers.push(rosreestrLayer)
        })
    }
    return newRosreestrLayers
}

export function getGismeteoPrecipitationLayer(timeMarkIndex: number, time: string) {
    // Убрать миллисекунды
    let currentTimeWithoutMilliseconds = time.slice(0, 19);
    return new TileLayer({
        preload: Infinity,
        zIndex: 400,
        source: new XYZ({
            tileUrlFunction: function ([z, x, y]) {
                return `https://api.gismeteo.ng/v3/tile-generator/maps/precipitation/nowcast/${z}/${timeMarkIndex}/${x}x${y}.png?${currentTimeWithoutMilliseconds}`;
            }
        }),
        opacity: 0.8
    });
}

// Основной слой дорожной сети
export function getRosreestrLabelsLayers() {
    const rosreestrLayersDisplayState: any = store.getState().mapSettings.rosreestrLayersDisplayState
    const rosreestrLayersOpacityState: any = store.getState().mapSettings.rosreestrLayersOpacityState
    let rosreestrLabelsLayers: any = []
    if (rosreestrLayersDisplayState) {
        const transformToLayerIndex = (labelsLayerIndex: string) => labelsLayerIndex.replace('Labels', '')
        const rosreestrLabelsLayersActiveItemsFilter = (layerIndex: any) => rosreestrLayersDisplayState[layerIndex] && layerIndex.includes('Labels')
            && rosreestrLayersDisplayState[transformToLayerIndex(layerIndex)]
        const rosreestrLabelsLayersDisplayStateActiveItems = Object.keys(rosreestrLayersDisplayState).filter(rosreestrLabelsLayersActiveItemsFilter)
        rosreestrLabelsLayersDisplayStateActiveItems.map((labelsLayerIndex: any) => {
            const baseKey = transformToLayerIndex(labelsLayerIndex)
            const labelsLayerName: any = (rosreestrLayersSettings as any)[baseKey]?.labelsName
            const layerOpacity = typeof rosreestrLayersOpacityState?.[baseKey] === 'number' ? rosreestrLayersOpacityState[baseKey] : 1
            const rosreestrLabelLayer = new TileLayer({
                preload: Infinity,
                zIndex: 600,
                className: `rosreestr-${baseKey}-labels`,
                opacity: layerOpacity,
                source: new TileWMS({
                    serverType: 'geoserver',
                    url: `https://nspd.gov.ru/api/aeggis/v4/${labelsLayerName}/wms`,
                    params: {
                        'VERSION': '1.3.0',
                        'LAYERS': labelsLayerName,
                        'TILED': 'true',
                        'FORMAT': 'image/png',
                        'CRS': 'EPSG:3857',
                        'dpi': getImageScale().dpi?.toString()
                    },
                })
            });
            rosreestrLabelsLayers.push(rosreestrLabelLayer)
        })
    }
    return rosreestrLabelsLayers
}

// Универсальный контроллер загрузки пропущенных тайлов
export const reloadController: any = {
    queue: {},						// Очередь неудачных по которым отправлены повторные запросы
    attemptsLoadNumber: 5,			// Количество попыток загрузки на 1 тайл
    attemptsDelay: 1000,
    counter: 1,	// Задержка мс перед повторной попыткой (можно 0)
    onTileLoadError: function (event: any, showRosreestrAlerts: any) {
        // Идентификатор тайла на основе координат
        let uid = (event && event.tile.tileCoord) ? event.tile.tileCoord.join(".") : undefined;
        showRosreestrAlerts(this.counter++)
        // Для нового тайла с ошибкой заведём место в массиве проблемных
        if (!reloadController.queue[uid])
            reloadController.queue[uid] = 1;	// Один раз он уже дал сбой
        // Если разрешено ещё сделать попытку - пробуем
        if (reloadController.queue[uid] <= reloadController.attemptsLoadNumber) {
            // Запланировать следующую попытку
            setTimeout(
                function () {
                    // Увеличить значение счётчика загрузок
                    reloadController.queue[uid]++;
                    console.warn("Повторная загрузка...");
                    event.tile.load();
                },
                reloadController.attemptsDelay
            )
        } else {
            // Попыток не осталось. Превышено число попыток загрузки тайла.
            // Вывести сообщение об ошибке.
            console.error("Превышено число попыток загрузки слоя...");
        }
    }
}

export function getGeoMarkerLayer() {
    const geoMarkerImg = getIndorImageSrc(IndorImages.Geomarker, 20);
    return new VectorLayer({
        zIndex: 8000,
        source: new VectorSource({
            features: [
                new Feature({
                    geometry: new Point([0, 0])
                })
            ]
        }),
        style: new Style({
            image: new Icon({ src: geoMarkerImg }),
        })
    });
}

export function getGeoJSONFeaturesVectorLayer(geoJSON: any, featureProjection: string, style?: any) {
    let features: any = []
    if (geoJSON) {
        features = new GeoJSON().readFeatures(geoJSON, {
            featureProjection: featureProjection,
        })
    }
    let vectorSource = new VectorSource({
        features: features
    });
    return new VectorLayer({
        zIndex: 900,
        source: vectorSource,
        style: style ?? getStyleForBasicFeature,
    });
}

export function getGeoJSONFeaturesVectorLayers(geoJSONArray: any, featureProjection: string, style?: any) {
    let vectorLayers = []
    for (const geoJSON of geoJSONArray) {
        let features: any = []
        if (geoJSON) {
            features = new GeoJSON().readFeatures(geoJSON, {
                featureProjection: featureProjection,
            })
        }
        let vectorSource = new VectorSource({
            features: features
        });
        const vectorLayer = new VectorLayer({
            zIndex: 900,
            source: vectorSource,
            style: style ?? getStyleForBasicFeature,
        });
        vectorLayers.push(vectorLayer)
    }
    return vectorLayers
}

export function getWKTFeatureVectorLayer(wkt: any, style?: any) {
    let features: any = []
    if (wkt && Array.isArray(wkt)) {
        wkt.map(wktString => {
            const feature = new WKT().readFeature(wktString, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857',
            });
            features.push(feature)
        })
    }
    if (wkt && !Array.isArray(wkt)) {
        features = new WKT().readFeatures(wkt, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
        })
    }
    const vectorSource = new VectorSource({
        features: features
    });
    return new VectorLayer({
        zIndex: 900,
        source: vectorSource,
        style: style ?? getStyleForBasicFeature,
    });
}

export function getPanoramaVideoLayer(wmsServer: string, wmsVersion: string, workspace: string, layerFilter?: string) {
    return new TileLayer({
        preload: Infinity,
        zIndex: 300,
        source: new TileWMS({
            serverType: 'geoserver',
            url: wmsServer + '/wms',
            params: {
                'SERVICE': 'WMS',
                'VERSION': wmsVersion,
                'REQUEST': 'GetMap',
                'FORMAT': 'image/png',
                'TRANSPARENT': true,
                'LAYERS': workspace + ':' + "MARoadVideoPosition",
                'TILED': true,
                'STYLES': '',
                'SRS': 'EPSG:3857',
                'dpi': getImageScale().dpi.toString(),
                'CQL_FILTER': layerFilter,
            },
        })
    })
}

export function getCameraMarkerLayer(position: any, style: any) {
    let feature = new Feature({
        geometry: new Point(position),
    })
    return new VectorLayer({
        zIndex: 10000,
        source: new VectorSource({
            features: [feature],
        }),
        style: style
    })
}
